import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './EsqueceuSenha.css';
import { getApiBaseUrl } from './utils/apiConfig';
import { Mail, ArrowLeft } from 'lucide-react';
import ProtectedBrandLogo from './components/ProtectedBrandLogo';

const API_BASE_URL = getApiBaseUrl();
const DEBOUNCE_MS = 400;

const EsqueceuSenha = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const [validating, setValidating] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateEmail = useCallback(async (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      setCanSubmit(false);
      return;
    }
    setValidating(true);
    setCanSubmit(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password-validate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ e: trimmed })
      });
      const data = await res.json();
      setCanSubmit(!!data.ok);
    } catch {
      setCanSubmit(false);
    } finally {
      setValidating(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (email.trim()) validateEmail(email);
      else setCanSubmit(false);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [email, validateEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password-send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ e: email.trim() })
      });
      const data = await res.json();
      if (data.ok) setSuccess(true);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    document.body.classList.add('login-novo-active');
    return () => document.body.classList.remove('login-novo-active');
  }, []);

  return (
    <div className="login-novo-page esqueceu-senha-page">
      <div className="login-novo-glow" aria-hidden />
      <div className="login-novo-shell">
        <div className="login-novo-brand">
          <ProtectedBrandLogo />
        </div>

        <div className="login-novo-card esqueceu-senha-card">
          <div className="login-novo-header">
            <h1>Esqueceu sua senha?</h1>
            <p>{success ? '' : 'Informe o e-mail de trabalho para receber o link'}</p>
          </div>

          {success ? (
            <div className="esqueceu-senha-success">
              <p className="esqueceu-senha-success-message">Confira seu e-mail e abra o link para redefinir sua senha.</p>
              <button
                type="button"
                className="login-novo-forgot-link esqueceu-senha-back"
                onClick={() => navigate('/')}
              >
                <ArrowLeft size={14} />
                Voltar ao login
              </button>
            </div>
          ) : (
            <form className="login-novo-form" onSubmit={handleSubmit}>
              <div className="login-novo-field">
                <label htmlFor="esqueceu-email">E-mail</label>
                <div className="login-novo-input-wrapper">
                  <Mail size={15} className="login-novo-input-icon" />
                  <input
                    type="email"
                    id="esqueceu-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email de trabalho"
                    className="login-novo-input"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              {sending && (
                <div className="esqueceu-senha-progress" role="status" aria-label="Enviando">
                  <div className="esqueceu-senha-progress-track">
                    <div className="esqueceu-senha-progress-fill" />
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="login-novo-submit"
                disabled={!canSubmit || validating || sending}
              >
                {sending ? (
                  <>
                    <i className="bi bi-arrow-repeat login-novo-spinner" />
                    Enviando...
                  </>
                ) : (
                  'Enviar o link para mim'
                )}
              </button>

              <button
                type="button"
                className="login-novo-forgot-link esqueceu-senha-back"
                onClick={() => navigate('/')}
              >
                <ArrowLeft size={14} />
                Voltar ao login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EsqueceuSenha;
